"use client";

import React from "react";
import {
    Box,
    LoadingOverlay,
    Text,
    Stack,
    TextInput,
    Select,
    useMantineColorScheme,
    Tabs,
} from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { Layout, Info, Shield } from "lucide-react";
import type {
    Chart,
    Report,
    GridPosition,
    ReportQueryResult,
} from "@bi-studio/shared/lib";
import { DashboardGridLayout } from "@/components/dashboards";
import { RoleSelector } from "@/components/common/RoleSelector";
import { ConfigTabs } from "@/components/common/ConfigTabs";

export interface DashboardEditViewProps {
    isSaving: boolean;
    form: UseFormReturnType<any>; // Using any here to accommodate both Input and Update types if needed
    charts: Chart[];
    reports: Report[];
    selectedCharts: Chart[];
    setSelectedCharts: React.Dispatch<React.SetStateAction<Chart[]>>;
    selectedReports: Report[];
    setSelectedReports: React.Dispatch<React.SetStateAction<Report[]>>;
    chartLayouts: Map<string, GridPosition>;
    setChartLayouts: React.Dispatch<React.SetStateAction<Map<string, GridPosition>>>;
    reportLayouts: Map<string, GridPosition>;
    setReportLayouts: React.Dispatch<React.SetStateAction<Map<string, GridPosition>>>;
    reportData: Map<string, ReportQueryResult>;
    setReportData: React.Dispatch<React.SetStateAction<Map<string, ReportQueryResult>>>;
    loadingReports: Set<string>;
    token: string;
}

export function DashboardEditView({
    isSaving,
    form,
    charts,
    reports,
    selectedCharts,
    setSelectedCharts,
    selectedReports,
    setSelectedReports,
    chartLayouts,
    setChartLayouts,
    reportLayouts,
    setReportLayouts,
    reportData,
    setReportData,
    loadingReports,
    token,
}: DashboardEditViewProps) {
    const { colorScheme } = useMantineColorScheme();

    const handleAddChart = (value: string | null) => {
        if (value) {
            const chartId = value;
            const chart = charts.find((c) => c.id === chartId);
            if (chart && !selectedCharts.find((c) => c.id === chartId)) {
                setSelectedCharts((prev) => [...prev, chart]);
                
                // Calculate new position
                const allLayouts = Array.from(chartLayouts.values()).concat(
                    Array.from(reportLayouts.values())
                );
                let newX = 0;
                let newY = 0;
                const defaultW = 6;
                const defaultH = 4;

                if (allLayouts.length > 0) {
                    const maxY = Math.max(...allLayouts.map((l) => l.y + l.h));
                    newY = maxY;
                }

                setChartLayouts((prev) => {
                    const next = new Map(prev);
                    next.set(chartId, { x: newX, y: newY, w: defaultW, h: defaultH });
                    return next;
                });
            }
        }
    };

    const handleAddReport = (value: string | null) => {
        if (value) {
            const reportId = value;
            const report = reports.find((r) => r.id === reportId);
            if (report && !selectedReports.find((r) => r.id === reportId)) {
                setSelectedReports((prev) => [...prev, report]);
                
                // Calculate new position
                const allLayouts = Array.from(chartLayouts.values()).concat(
                    Array.from(reportLayouts.values())
                );
                let newX = 0;
                let newY = 0;
                const defaultW = 6;
                const defaultH = 4;

                if (allLayouts.length > 0) {
                    const maxY = Math.max(...allLayouts.map((l) => l.y + l.h));
                    newY = maxY;
                }

                setReportLayouts((prev) => {
                    const next = new Map(prev);
                    next.set(reportId, { x: newX, y: newY, w: defaultW, h: defaultH });
                    return next;
                });
            }
        }
    };

    return (
        <Box
            style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'var(--mantine-color-white)',
                borderRadius: 'var(--mantine-radius-md)',
                border: '1px solid var(--modal-border)',
                backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined,
                boxShadow: 'var(--mantine-shadow-sm)',
                overflow: 'hidden',
                position: 'relative'
            }}
        >
            <LoadingOverlay visible={isSaving} overlayProps={{ blur: 2 }} />
            
            <Box p="md">
                <ConfigTabs
                    defaultValue="general"
                    tabs={[
                        { value: 'general', label: 'General', icon: <Info size={16} /> },
                        { value: 'layout', label: 'Layout', icon: <Layout size={16} /> },
                        { value: 'permissions', label: 'Permissions', icon: <Shield size={16} /> },
                    ]}
                >
                    <Tabs.Panel value="general">
                        <Stack gap="md">
                            <TextInput
                                label="Dashboard Name"
                                placeholder="Sales Dashboard"
                                disabled={isSaving}
                                required
                                {...form.getInputProps("name")}
                            />

                            <TextInput
                                label="Description"
                                placeholder="Overview of sales metrics"
                                disabled={isSaving}
                                {...form.getInputProps("description")}
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="permissions">
                        <Stack gap="md">
                            <RoleSelector
                                value={form.values.allowedRoles || []}
                                onChange={(roles) => form.setFieldValue("allowedRoles", roles)}
                                disabled={isSaving}
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="layout">
                        <Stack gap="md">
                            <Stack gap="sm">
                                <Stack gap="xs">
                                    <Text fw={500}>Add Charts</Text>
                                    <Select
                                        placeholder="Select a chart to add..."
                                        data={charts
                                            .filter((chart) => !selectedCharts.find((c) => c.id === chart.id))
                                            .map((chart) => ({
                                                value: chart.id,
                                                label: `${chart.config.title || `Chart ${chart.id}`} (${chart.type})`
                                            }))}
                                        onChange={handleAddChart}
                                        searchable
                                        clearable
                                        disabled={isSaving || charts.length === 0}
                                        style={{ flex: 1 }}
                                    />
                                </Stack>

                                <Stack gap="xs">
                                    <Text fw={500}>Add Reports</Text>
                                    <Select
                                        placeholder="Select a report to add..."
                                        data={reports
                                            .filter((report) => !selectedReports.find((r) => r.id === report.id))
                                            .map((report) => ({
                                                value: report.id,
                                                label: report.name
                                            }))}
                                        onChange={handleAddReport}
                                        searchable
                                        clearable
                                        disabled={isSaving || reports.length === 0}
                                        style={{ flex: 1 }}
                                    />
                                </Stack>
                            </Stack>

                            {(selectedCharts.length > 0 || selectedReports.length > 0) ? (
                                <Stack gap="xs">
                                    <Text fw={500}>Dashboard Layout</Text>
                                    <Text size="sm" c="dimmed">
                                        Drag and resize items to arrange them. Click and drag the header to move, drag corners to resize.
                                    </Text>
                                    <Box
                                        className="rounded-lg p-4"
                                        style={{
                                            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                                            border: '1px solid var(--modal-border)'
                                        }}
                                    >
                                        <DashboardGridLayout
                                            charts={selectedCharts}
                                            reports={selectedReports}
                                            chartLayouts={chartLayouts}
                                            reportLayouts={reportLayouts}
                                            onChartLayoutChange={(chartId, layout) => {
                                                setChartLayouts((prev) => {
                                                    const next = new Map(prev);
                                                    next.set(chartId, layout);
                                                    return next;
                                                });
                                            }}
                                            onReportLayoutChange={(reportId, layout) => {
                                                setReportLayouts((prev) => {
                                                    const next = new Map(prev);
                                                    next.set(reportId, layout);
                                                    return next;
                                                });
                                            }}
                                            onRemoveChart={(chartId) => {
                                                setSelectedCharts((prev) => prev.filter((c) => c.id !== chartId));
                                                setChartLayouts((prev) => {
                                                    const next = new Map(prev);
                                                    next.delete(chartId);
                                                    return next;
                                                });
                                            }}
                                            onRemoveReport={(reportId) => {
                                                setSelectedReports((prev) => prev.filter((r) => r.id !== reportId));
                                                setReportLayouts((prev) => {
                                                    const next = new Map(prev);
                                                    next.delete(reportId);
                                                    return next;
                                                });
                                                setReportData((prev) => {
                                                    const next = new Map(prev);
                                                    next.delete(reportId);
                                                    return next;
                                                });
                                            }}
                                            reportData={reportData}
                                            loadingReports={loadingReports}
                                            token={token}
                                        />
                                    </Box>
                                </Stack>
                            ) : (
                                <Box py="xl" style={{ textAlign: 'center' }}>
                                    <Text c="dimmed">No items added to dashboard. Select charts or reports from above to start building.</Text>
                                </Box>
                            )}
                        </Stack>
                    </Tabs.Panel>
                </ConfigTabs>
            </Box>
        </Box>
    );
}
